<template>
    <div class="plan-vs-real container mt-4">
        <h3 class="mb-4 text-primary">
            <font-awesome-icon icon="chart-line" class="me-2" />
            Comparación: Plan vs Real
        </h3>

        <div v-if="loading" class="text-center my-5">
            <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Cargando...</span>
            </div>
        </div>

        <div v-else>
            <!-- FILTROS CLAROS -->
            <div class="mb-3 d-flex flex-column gap-2">
                <label class="form-switch d-flex align-items-center gap-2">
                    <input type="checkbox" class="form-check-input" v-model="mostrarSinEjecucion" />
                    <span>Mostrar actividades planificadas sin ejecución</span>
                </label>

                <label class="form-switch d-flex align-items-center gap-2">
                    <input type="checkbox" class="form-check-input" v-model="mostrarNoPlanificadas" />
                    <span>Mostrar tareas ejecutadas que no estaban planificadas</span>
                </label>
            </div>


            <!-- ESTADÍSTICAS -->
            <EstadisticasGenerales :planificadas="actividadesPlanificadas"
                :ejecutadasPlanificadas="tareasEjecutadasPlanificadas"
                :ejecutadasNoPlanificadas="tareasEjecutadasNoPlanificadas" :simbolo="monedaActual.simbolo" />

            <!-- LISTADO ACTIVIDADES -->
            <div v-if="actividadesFiltradas.length === 0" class="alert alert-info mt-4">
                No hay actividades que coincidan con los filtros seleccionados.
            </div>

            <div v-for="actividad in actividadesFiltradas" :key="actividad.id"
                :class="['plan-task-card', { compact: !actividad.tareas.length }]">

                <div class="plan-task-header d-flex justify-content-between align-items-center">
                    <h5 class="titulo-actividad mb-0">
                        {{ actividad.nombre_tarea.nombre || 'Sin nombre' }}

                        <!-- Ejecutada y Planificada -->
                        <span v-if="actividad.fue_planificada && actividad.tiene_ejecucion"
                            class="badge bg-success ms-2">
                            Ejecutada planificada
                        </span>

                        <!-- Planificada sin ejecución -->
                        <span v-else-if="actividad.fue_planificada && !actividad.tiene_ejecucion"
                            class="badge bg-secondary ms-2">
                            Planificada sin ejecución
                        </span>

                        <!-- Ejecutada no planificada -->
                        <span v-else-if="!actividad.fue_planificada && actividad.tiene_ejecucion"
                            class="badge bg-warning text-dark ms-2">
                            Ejecutada no planificada
                        </span>

                        <!-- Sin ejecución y no planificada (caso raro) -->
                        <span v-else class="badge bg-light text-muted border ms-2">
                            No planificada sin ejecución
                        </span>

                        <span v-if="actividad.desviacion > 0" class="badge bg-danger ms-2">Desvío</span>
                    </h5>

                </div>

                <div class="plan-meta mb-2">
                    <span><strong>Categoría:</strong> {{ actividad.categoria_tarea }}</span>
                    <span><strong>Etapa:</strong>
                        <span class="etapa-badge">{{ actividad.etapa_evento_cultivo }}</span>
                    </span>
                </div>

                <div class="plan-vs-real-boxes row">
                    <div class="col-md-6">
                        <PlanCard :actividad="actividad" :simbolo="monedaActual.simbolo" />
                    </div>
                    <div class="col-md-6">
                        <RealCard :actividad="actividad" :simbolo="monedaActual.simbolo" @ver="verTarea" />
                    </div>
                </div>

                <div class="desviacion-costo mt-2">
                    <strong>Desviación:</strong>
                    <span
                        :class="{ 'text-success': actividad.desviacion <= 0, 'text-danger': actividad.desviacion > 0 }">
                        {{ monedaActual.simbolo }}{{ formatearMonto(actividad.desviacion) }}
                    </span>
                </div>
            </div>
        </div>

        <!-- MODAL -->
        <Teleport to="body">
            <div v-if="mostrarModalTarea" class="modal-overlay" @click.self="cerrarModal">
                <div class="modal-content position-relative">
                    <button class="btn-close-modal" @click="cerrarModal" aria-label="Cerrar">
                        <font-awesome-icon icon="times" />
                    </button>
                    <TaskCard :tarea="tareaSeleccionada" :soloLectura="true" />
                </div>
            </div>
        </Teleport>
    </div>
</template>

<script>
import axios from '@/axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import TaskCard from '@/components/Tarea/listarTareas/TaskCard.vue';
import EstadisticasGenerales from './EstadisticasGenerales.vue';
import PlanCard from './PlanCard.vue';
import RealCard from './RealCard.vue';

export default {
    name: 'PlanVsReal',
    components: { FontAwesomeIcon, TaskCard, PlanCard, RealCard, EstadisticasGenerales },
    props: {
        plan: { type: Object, required: false }
    },
    data() {
        return {
            loading: true,
            actividadesPlanificadas: [],
            tareasEjecutadasPlanificadas: [],
            tareasEjecutadasNoPlanificadas: [],
            proyectoActual: this.$store.getters.getProyectoActual,
            clubActual: this.$store.getters.getClubActual,
            filtroEtapa: '',
            mostrarSinEjecucion: true,
            mostrarNoPlanificadas: true,
            monedaActual: { simbolo: '$', nombre: 'ARS' },
            tareaSeleccionada: null,
            mostrarModalTarea: false
        };
    },
    computed: {
        actividadesFiltradas() {
            let todas = [];

            // Planificadas (del plan)
            const plan = this.actividadesPlanificadas.map((a, i) => ({
                ...a,
                id: `plan-${i}`,
                fue_planificada: true,
                tiene_ejecucion: false,
                tareas: [],
                desviacion: 0,
                es_no_planificada: false
            }));

            // Ejecutadas planificadas
            const ejecutadasPlan = this.tareasEjecutadasPlanificadas.map((t, i) => ({
                ...t,
                id: `ejp-${i}`,
                fue_planificada: true,
                tiene_ejecucion: true,
                desviacion: (t.costo_final_usd || 0) - (t.costo_estimado_usd || 0),
                tareas: [t],
                es_no_planificada: false
            }));

            // Ejecutadas no planificadas
            const ejecutadasNoPlan = this.tareasEjecutadasNoPlanificadas.map((t, i) => ({
                ...t,
                id: `ejnp-${i}`,
                fue_planificada: false,
                tiene_ejecucion: true,
                desviacion: t.costo_final_usd || 0,
                tareas: [t],
                es_no_planificada: true
            }));

            todas = [...plan, ...ejecutadasPlan, ...ejecutadasNoPlan];

            return todas.filter(a => {
                const coincideEtapa = this.filtroEtapa ? a.etapa_evento_cultivo === this.filtroEtapa : true;
                const tieneEjecucion = a.tiene_ejecucion;
                const incluirPorEjecucion = this.mostrarSinEjecucion || tieneEjecucion;
                const incluirPorPlanificacion = this.mostrarNoPlanificadas || a.fue_planificada;
                return coincideEtapa && incluirPorEjecucion && incluirPorPlanificacion;
            });
        }
    },
    async created() {
        try {
            await this.obtenerMonedaActual();
            const plan_cultivo_id = this.proyectoActual?.plan_cultivo_id;
            const proyecto_id = this.proyectoActual?.id;
            if (!plan_cultivo_id) return;

            const res = await axios.get(`/api/plan_vs_real/${plan_cultivo_id}`, {
                params: { proyecto_id }
            });

            this.actividadesPlanificadas = res.data.planificadas || [];
            this.tareasEjecutadasPlanificadas = res.data.ejecutadas_planificadas || [];
            this.tareasEjecutadasNoPlanificadas = res.data.ejecutadas_no_planificadas || [];
        } catch (e) {
            console.error('Error al cargar plan vs real:', e);
        } finally {
            this.loading = false;
        }
    },
    methods: {
        async obtenerMonedaActual() {
            try {
                const res = await axios.get('/api/moneda_actual', { params: { club_id: this.clubActual.id } });
                this.monedaActual = res.data;
            } catch {
                console.warn('No se pudo obtener la moneda actual.');
            }
        },
        verTarea(tarea) {
            this.tareaSeleccionada = tarea;
            this.mostrarModalTarea = true;
        },
        cerrarModal() {
            this.tareaSeleccionada = null;
            this.mostrarModalTarea = false;
        },
        formatearMonto(valor) {
            return typeof valor === 'number' ? valor.toFixed(2) : '0.00';
        }


    }
};
</script>

<style scoped>
.plan-vs-real h3 {
    font-weight: 600;
}

.plan-task-card {
    background-color: #fff;
    border-radius: 8px;
    border-left: 5px solid var(--app-primary, #4e73df);
    padding: 1rem;
    margin-bottom: 1rem;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.plan-task-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.titulo-actividad {
    font-size: 1.1rem;
    font-weight: 600;
    color: #4e73df;
    margin: 0;
}

.plan-meta {
    display: flex;
    justify-content: space-between;
    font-size: 0.85rem;
    color: #666;
}

.plan-meta .etapa-badge {
    background-color: #d1e7dd;
    color: #0f5132;
    padding: 2px 8px;
    border-radius: 8px;
    font-size: 0.75rem;
    font-weight: bold;
}

.compact {
    padding: 0.5rem 1rem;
    background-color: #f4f4f4;
    opacity: 0.7;
}

.modal-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.modal-content {
    background: white;
    border-radius: 8px;
    padding: 1rem;
    max-width: 600px;
    width: 90%;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.25);
}

.btn-close-modal {
    position: absolute;
    top: 0.5rem;
    right: 0.5rem;
    background: transparent;
    border: none;
    font-size: 1.25rem;
    color: #333;
    cursor: pointer;
}

.btn-toggle {
    font-weight: 600;
    transition: all 0.2s ease-in-out;
    border-width: 2px;
}

.btn-toggle.active {
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.btn-outline-warning.active {
    background-color: #ffc107;
    color: #000;
    border-color: #ffc107;
}
</style>
